import { useState, useEffect, useCallback } from 'react';
import { Bell, ChevronDown, ChevronUp, Megaphone, Cloud, Calendar, FileText, CheckCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Drawer, DrawerContent, DrawerTrigger } from './ui/drawer';
import { useIsMobile } from './ui/use-mobile';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import type { Notification } from '../types';
import { openArchiveTarget } from '../utils/notificationLinks';

const ICON_MAP: Record<string, typeof Megaphone> = {
  announcement: Megaphone,
  class_mode: Cloud,
  holiday: Calendar,
  shared_document: FileText,
};

const COLOR_MAP: Record<string, string> = {
  announcement: 'bg-blue-100 text-blue-600',
  class_mode: 'bg-purple-100 text-purple-600',
  holiday: 'bg-amber-100 text-amber-600',
  shared_document: 'bg-emerald-100 text-emerald-600',
};

const LABEL_MAP: Record<string, string> = {
  announcement: 'Announcement',
  class_mode: 'Class Mode',
  holiday: 'Holiday',
  shared_document: 'Document',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatNotificationDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isLongNotification(message: string): boolean {
  return message.length > 140 || message.includes('\n');
}

function getArchiveTarget(notification: Notification) {
  if (notification.type !== 'shared_document') {
    return null;
  }

  const documentId = notification.documentId || notification.referenceId;
  if (!documentId) {
    return null;
  }

  return {
    documentId,
    commentId: notification.commentId,
  };
}

export function NotificationBell() {
  const { accessToken } = useAuth();
  const isMobile = useIsMobile();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const unreadCount = notifications.filter(notification => !notification.isRead).length;
  const notificationSummary = `${unreadCount} notification${unreadCount === 1 ? '' : 's'}`;

  const fetchNotifications = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      const data = await api.getNotifications(accessToken);
      const raw: Notification[] = data.notifications ?? [];
      setNotifications(
        raw.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      );
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const dismiss = async (id: string) => {
    if (!accessToken) return;
    setNotifications(prev => prev.map(notification => (
      notification.id === id
        ? { ...notification, isRead: true }
        : notification
    )));
    setSelectedNotification(prev => (
      prev?.id === id
        ? { ...prev, isRead: true }
        : prev
    ));
    try { await api.markNotificationRead(id, accessToken); } catch { /* local state already updated */ }
  };

  const dismissAll = async () => {
    if (!accessToken) return;
    setNotifications(prev => prev.map(notification => ({ ...notification, isRead: true })));
    setSelectedNotification(prev => (prev ? { ...prev, isRead: true } : prev));
    try { await api.markAllNotificationsRead(accessToken); } catch { /* local state already updated */ }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => (
      prev.includes(id)
        ? prev.filter(expandedId => expandedId !== id)
        : [...prev, id]
    ));
  };

  const openLinkedNotification = (notification: Notification) => {
    const archiveTarget = getArchiveTarget(notification);

    if (archiveTarget) {
      openArchiveTarget(archiveTarget);
      setOpen(false);
      setSelectedNotification(null);
      return;
    }

    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  const selectedIcon = selectedNotification ? (ICON_MAP[selectedNotification.type] ?? Bell) : Bell;
  const selectedColor = selectedNotification ? (COLOR_MAP[selectedNotification.type] ?? 'bg-gray-100 text-gray-600') : 'bg-gray-100 text-gray-600';
  const selectedLabel = selectedNotification ? (LABEL_MAP[selectedNotification.type] ?? selectedNotification.type) : 'Notification';
  const selectedArchiveTarget = selectedNotification ? getArchiveTarget(selectedNotification) : null;

  /* ── Bell button ── */
  const bellButton = (
    <Button
      variant="outline"
      size="sm"
      className={`relative border-slate-200 bg-white/95 p-0 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md focus-visible:ring-2 focus-visible:ring-blue-300 ${
        unreadCount > 0 ? 'ring-1 ring-blue-200/80' : ''
      } ${
        isMobile ? 'h-10 w-10 rounded-2xl' : 'h-9 w-9 rounded-xl lg:h-10 lg:w-10'
      }`}
      aria-label={`Notifications${unreadCount > 0 ? ` (${notificationSummary})` : ''}`}
      aria-expanded={open}
    >
      <Bell className={`text-slate-700 transition-colors ${isMobile ? 'h-[18px] w-[18px]' : 'h-4 w-4 lg:h-[18px] lg:w-[18px]'} ${
        unreadCount > 0 ? 'text-blue-700' : ''
      }`} />
      {loading && (
        <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-white" aria-hidden="true" />
      )}
      {unreadCount > 0 && (
        <span className="absolute left-[calc(100%-0.45rem)] top-0 z-10 flex min-h-5 min-w-5 -translate-y-1/3 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white shadow-md shadow-red-500/30">
          {unreadCount}
        </span>
      )}
    </Button>
  );

  /* ── Shared notification list ── */
  const notificationList = (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[inherit] bg-white">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-blue-50/60 px-4 py-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
              {unreadCount > 0 && (
                <Badge className="rounded-full border-0 bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 shadow-sm">
                  {notificationSummary}
                </Badge>
              )}
              {loading && notifications.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                  Refreshing
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Latest updates for your account, classes, and shared records.
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 rounded-lg px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-100/70 hover:text-blue-800"
              onClick={dismissAll}
            >
              <CheckCheck className="mr-1 h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <ScrollArea className={isMobile ? 'min-h-0 flex-1' : 'h-[min(28rem,70vh)]'}>
        {loading && notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-5 py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            </div>
            <p className="text-sm font-medium text-slate-700">Loading notifications</p>
            <p className="text-xs text-slate-500">Checking for the latest updates.</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-5 py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Bell className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700">All caught up</p>
            <p className="max-w-[18rem] text-xs leading-relaxed text-slate-500">
              New announcements and document activity will appear here as soon as they arrive.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {notifications.map(n => {
              const Icon = ICON_MAP[n.type] ?? Bell;
              const color = COLOR_MAP[n.type] ?? 'bg-gray-100 text-gray-600';
              const label = LABEL_MAP[n.type] ?? n.type;
              const expanded = expandedIds.includes(n.id);
              const canExpand = isLongNotification(n.message);
              const archiveTarget = getArchiveTarget(n);

              return (
                <li key={n.id} className={`group relative flex gap-3 px-4 py-3.5 transition-colors hover:bg-slate-50/90 sm:px-5 ${n.isRead ? 'bg-slate-50/40' : ''}`}>
                  <span className={`absolute left-0 top-4 h-8 w-1 rounded-r-full ${n.isRead ? 'bg-slate-300/80' : 'bg-blue-500/80'}`} aria-hidden="true" />
                  {/* Icon */}
                  <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm ${color}`}>
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Content — single column, no nesting */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                        {label}
                      </span>
                      <span className="text-[11px] font-medium text-slate-400">
                        {timeAgo(n.createdAt)}
                      </span>
                    </div>

                    <p className={`mt-2 text-[13px] leading-snug break-words sm:text-sm ${n.isRead ? 'font-medium text-slate-700' : 'font-semibold text-slate-900'}`}>
                      {n.title}
                    </p>

                    <p className={`mt-1 whitespace-pre-wrap text-xs leading-relaxed break-words ${n.isRead ? 'text-slate-400' : 'text-slate-500'} ${expanded ? '' : 'line-clamp-2'}`}>
                      {n.message}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {canExpand && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 rounded-lg px-3 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          onClick={() => toggleExpanded(n.id)}
                        >
                          {expanded ? (
                            <ChevronUp className="mr-1 h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="mr-1 h-3.5 w-3.5" />
                          )}
                          {expanded ? 'Collapse' : 'Expand'}
                        </Button>
                      )}
                      {archiveTarget ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-lg border-slate-200 px-3 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300"
                          onClick={() => openLinkedNotification(n)}
                        >
                          {archiveTarget.commentId ? 'Open comment' : 'Open document'}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-lg border-slate-200 px-3 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300"
                          onClick={() => setSelectedNotification(n)}
                        >
                          View details
                        </Button>
                      )}
                      {!n.isRead && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-lg border-blue-200 px-3 text-[11px] font-medium text-blue-700 transition-colors hover:bg-blue-50 group-hover:border-blue-300"
                          onClick={() => dismiss(n.id)}
                        >
                          <CheckCheck className="mr-1 h-3 w-3" />
                          Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </div>
  );

  const notificationDetailsDialog = (
    <Dialog open={!!selectedNotification} onOpenChange={(nextOpen) => {
      if (!nextOpen) setSelectedNotification(null);
    }}>
      <DialogContent className="max-h-[85vh] max-w-xl gap-0 overflow-hidden border-slate-200 p-0 shadow-2xl">
        {selectedNotification && (
          <>
            <DialogHeader className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-blue-50/60 px-6 py-5 text-left">
              <div className="flex items-start gap-3 pr-8">
                <div className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${selectedColor}`}>
                  {(() => {
                    const SelectedIcon = selectedIcon;
                    return <SelectedIcon className="h-5 w-5" />;
                  })()}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="rounded-full border-0 bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600 shadow-none">
                      {selectedLabel}
                    </Badge>
                    <span className="text-xs font-medium text-slate-400">
                      {timeAgo(selectedNotification.createdAt)}
                    </span>
                  </div>
                  <DialogTitle className="mt-2 text-left text-base leading-snug text-slate-900">
                    {selectedNotification.title}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-left text-xs text-slate-500">
                    Received {formatNotificationDate(selectedNotification.createdAt)}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <ScrollArea className="h-[min(24rem,55vh)] px-6 py-5">
              <div className="space-y-5">
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Message</h4>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-slate-700 break-words">
                    {selectedNotification.message}
                  </p>
                </section>

                <section className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:grid-cols-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Audience</p>
                    <p className="mt-1 text-sm font-medium text-slate-700">{selectedNotification.audience}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Reference</p>
                    <p className="mt-1 break-all text-sm font-medium text-slate-700">
                      {selectedNotification.referenceId || 'No linked reference'}
                    </p>
                  </div>
                </section>
              </div>
            </ScrollArea>

            <DialogFooter className="border-t border-slate-200 px-6 py-4 sm:justify-between">
              <p className="text-xs text-slate-500">Use the details view for long updates and related record references.</p>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={() => setSelectedNotification(null)}
                >
                  Close
                </Button>
                {selectedArchiveTarget && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => openLinkedNotification(selectedNotification)}
                  >
                    {selectedArchiveTarget.commentId ? 'Open comment' : 'Open document'}
                  </Button>
                )}
                {!selectedNotification.isRead && (
                  <Button
                    type="button"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => {
                      void dismiss(selectedNotification.id);
                    }}
                  >
                    <CheckCheck className="mr-2 h-4 w-4" />
                    Mark as read
                  </Button>
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  /* ── Mobile: bottom drawer ── */
  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>{bellButton}</DrawerTrigger>
          <DrawerContent className="max-h-[88vh] overflow-hidden rounded-t-[1.75rem] px-0">
            {notificationList}
          </DrawerContent>
        </Drawer>
        {notificationDetailsDialog}
      </>
    );
  }

  /* ── Desktop: popover ── */
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{bellButton}</PopoverTrigger>
        <PopoverContent align="end" sideOffset={10} className="w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-slate-200 p-0 shadow-2xl shadow-slate-900/10">
          {notificationList}
        </PopoverContent>
      </Popover>
      {notificationDetailsDialog}
    </>
  );
}
